// src/features/delegations/hooks/useMyDelegations.js
//
// Regular-user hook — fetches only delegations where the current user is the
// delegator OR the recipient.  The backend filter supports AND logic per call,
// so we issue two parallel requests and merge the results client-side.
// Used by users who only have delegation:read (no delegation:create).
import { useState, useEffect, useCallback } from 'react';
import { delegationsAPI } from '../services/delegations';
import { useApi } from '../../../hooks/api/useApi';
import { tokenService } from '../../auth/services/tokenService';

const DEBOUNCE_MS = 400;

export function useMyDelegations(initialState = {}) {
  const { loading, error, execute, clearError } = useApi();

  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm || '');
  const [debouncedSearch, setDebouncedSearch] = useState(initialState.searchTerm || '');
  const [statuses, setStatuses] = useState(initialState.statuses || []);

  const [delegations, setDelegations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadDelegations = useCallback(async () => {
    const userId = tokenService.getUserId();
    if (!userId) return;

    const base = {};
    if (debouncedSearch.trim()) base.search_term = debouncedSearch.trim();
    if (statuses.length > 0) base.statuses = statuses;

    // Two parallel calls: delegations where user is delegator, and where recipient
    const [aselegator, asRecipient] = await Promise.all([
      execute(() => delegationsAPI.filterDelegations({ ...base, delegator_ids: [userId] })),
      execute(() => delegationsAPI.filterDelegations({ ...base, recipient_ids: [userId] })),
    ]);

    const delegatorList = aselegator.success
      ? (aselegator.data?.data?.delegations || [])
      : [];
    const recipientList = asRecipient.success
      ? (asRecipient.data?.data?.delegations || [])
      : [];

    // Merge and deduplicate by delegation_id
    const seen = new Set();
    const merged = [...delegatorList, ...recipientList].filter((d) => {
      if (seen.has(d.delegation_id)) return false;
      seen.add(d.delegation_id);
      return true;
    });

    setDelegations(merged);
    setTotalCount(merged.length);
  }, [debouncedSearch, statuses, execute]);

  useEffect(() => {
    loadDelegations();
  }, [loadDelegations]);

  const hasActiveFilters = searchTerm !== '' || statuses.length > 0;

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStatuses([]);
  }, []);

  return {
    delegations,
    totalCount,
    loading,
    error,
    clearError,
    searchTerm,
    statuses,
    hasActiveFilters,
    setSearchTerm,
    setStatuses,
    clearFilters,
    refreshData: loadDelegations,
  };
}
